import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { UMark } from "./UMark";
import { site } from "@/site.config";

const links = [
  { label: "About", href: "#about" },
  { label: "Building", href: "#building" },
  { label: "Path", href: "#path" },
  { label: "Notes", href: "#notes" },
  { label: "World Tour", href: "#rooms" },
  { label: "Contact", href: "#contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-full focus:bg-brand-orange focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white"
      >
        Skip to content
      </a>
      <header
        data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-300 ${
          scrolled
            ? "border-b border-white/10 bg-navy/95 backdrop-blur-sm"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 w-full max-w-[1200px] items-center justify-between px-6 lg:h-20">
          <a href="#top" data-testid="nav-home-link" className="flex items-center gap-3">
            <UMark size={30} on="navy" />
            <span className="text-sm font-semibold tracking-tight text-white">
              {site.name}
            </span>
          </a>

          <nav aria-label="Primary" className="hidden items-center gap-7 md:flex">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-[12px] font-medium uppercase tracking-[0.18em] text-white/70 transition-colors duration-200 hover:text-white"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              data-testid="nav-cta-start-conversation"
              className="rounded-full bg-brand-orange px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-200 hover:bg-brand-orange-light"
            >
              Start a conversation
            </a>
          </nav>

          <button
            type="button"
            data-testid="nav-menu-button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen(true)}
            className="text-white md:hidden"
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      {open && (
        <div
          data-testid="mobile-nav-drawer"
          className="fixed inset-0 z-[60] flex flex-col bg-navy px-6 pb-10 pt-5 md:hidden"
        >
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-3">
              <UMark size={30} on="navy" />
              <span className="text-sm font-semibold text-white">{site.name}</span>
            </span>
            <button
              type="button"
              data-testid="mobile-nav-close-button"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              className="text-white"
            >
              <X size={26} />
            </button>
          </div>
          <nav aria-label="Mobile" className="mt-14 flex flex-col gap-7">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setOpen(false)}
                className="text-3xl font-semibold tracking-tight text-white transition-colors duration-200 hover:text-brand-orange"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <a
            href="#contact"
            data-testid="mobile-nav-cta"
            onClick={() => setOpen(false)}
            className="mt-auto rounded-full bg-brand-orange px-6 py-4 text-center text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-200 hover:bg-brand-orange-light"
          >
            Start a conversation
          </a>
        </div>
      )}
    </>
  );
}
